/**
 * EnvelopeSentPage
 *
 * Post-send confirmation. Reached from EnvelopeReviewPage right after the
 * owner clicks Send and the API has flipped the envelope to `sent`.
 *
 * Purpose:
 *   - Acknowledge the action ("yes, it really sent — recipients are
 *     getting their email").
 *   - Tell the owner where to go next (status detail vs. envelopes list)
 *     so they aren't dumped back into the builder, which is the wrong
 *     surface for a non-draft envelope.
 *
 * No data mutations here — purely a confirmation screen. The envelope is
 * fetched read-only via `useEnvelope` for the recipient count + workspace
 * display; the data is already cached from the review page so the load is
 * effectively instant in the happy path.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useEnvelope, useRecipients } from '../hooks';
import { useSelectedHolder } from '../hooks/useSelectedHolder';

export function EnvelopeSentPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  const { envelope, loading } = useEnvelope(envelopeId);
  const { recipients } = useRecipients(envelopeId);

  // Header chip — same wiring as the other builder/review/detail pages.
  // Switching it changes the user's active workspace preference for
  // other work; it does not affect this envelope (sent envelopes are
  // pinned to the workspace they were created under).
  const { holderId: activeHolderId, setHolderId: setActiveHolderId } = useSelectedHolder();

  // Workspace display name (envelope's own holder, NOT the active chip).
  const switcher = useHolderSwitcher();
  const envelopeWorkspaceName =
    switcher.options.find((o) => o.holderId === envelope?.holderId)?.displayName ?? null;

  if (!envelopeId) return null;

  if (loading || !envelope) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-2xl mx-auto p-6 w-full space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const signerCount = recipients.filter((r) => r.role === 'signer').length;

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
      </ArcheHeader>
      <div className="max-w-2xl mx-auto p-6 w-full">
        <div
          className="flex flex-col items-center text-center gap-4 py-10"
          data-testid="envelope-sent-confirmation"
        >
          <div className="rounded-full bg-primary/10 p-4">
            <CheckCircle2 className="h-12 w-12 text-primary" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-semibold">Envelope sent</h1>
          <p className="max-w-md text-muted-foreground">
            "{envelope.title}" is on its way to{' '}
            <strong>
              {signerCount} {signerCount === 1 ? 'signer' : 'signers'}
            </strong>
            . They'll get an email with a unique signing link.
          </p>
          <p className="text-xs text-muted-foreground">
            Sent from <strong>{envelopeWorkspaceName ?? envelope.holderId}</strong>. You'll get an
            email notification each time a recipient signs, and another when everyone is done.
          </p>

          <div className="flex flex-wrap gap-2 justify-center pt-4">
            <Button
              onClick={() => navigate(`/a/arche_document_sign/envelope/${envelopeId}/detail`)}
            >
              <FileText className="h-4 w-4 mr-2" />
              View envelope status
            </Button>
            <Button variant="outline" onClick={() => navigate('/a/arche_document_sign')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to envelopes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
